import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { PromocodesListQuery, usePromocodesListQuery } from '@occc/graphql';
import { Button } from '@occc/ui-design-system';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { PromocodesIndividualViewAllPopup } from './promocodes-individual-view-all-popup.component';

import { messages } from '../../promocodes.messages';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;
const CODES_PAGE_INFO_PATH = 'offers.edges.0.node.earningMechanism.codes.pageInfo';

const getCodes = (data?: PromocodesListQuery) => {
  const earningMechanism = data?.offers.edges[0]?.node.earningMechanism;

  if (earningMechanism?.__typename !== 'EarningMechanismPromocodes') {
    return [];
  }

  return earningMechanism.codes.edges.map((edge) => edge.node);
};

type PromocodesIndividualViewAllProps = {
  offerId: string;
};

export const PromocodesIndividualViewAll = ({ offerId }: PromocodesIndividualViewAllProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>();

  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value.trim());
        setPaginationInfo(undefined);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const filter = useMemo(
    () => (debouncedSearch.length > 0 ? { search: debouncedSearch } : {}),
    [debouncedSearch],
  );

  const { data, loading, error, paginationProps } = usePaginatedQuery(
    usePromocodesListQuery,
    CODES_PAGE_INFO_PATH,
    PAGE_SIZE,
    {
      skip: !isOpen,
      variables: { offerId, filter },
      fetchPolicy: 'network-only',
      onError: () => {},
    },
    paginationInfo,
    setPaginationInfo,
  );

  const codes = useMemo(() => getCodes(data), [data]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    applySearch(value);
  };

  const handleClose = () => {
    applySearch.cancel();
    setIsOpen(false);
    setSearch('');
    setDebouncedSearch('');
    setPaginationInfo(undefined);
  };

  return (
    <>
      <Button variant='outlined' onClick={() => setIsOpen(true)}>
        {messages.viewAllCodesTitle}
      </Button>
      <PromocodesIndividualViewAllPopup
        isOpen={isOpen}
        onClose={handleClose}
        search={search}
        onSearchChange={handleSearchChange}
        codes={codes}
        isLoading={loading}
        hasError={Boolean(error)}
        paginationProps={paginationProps}
      />
    </>
  );
};
